import dayjs from 'dayjs';
import { z } from 'zod';

// REQ-7/REQ-8 (react-base/RULES.md § State management): a table's filters must survive a reload
// and be shareable by link, the same as the page number use-page-params.ts already keeps in the
// URL. Pure functions, not a hook — parsing or building a URLSearchParams needs no component
// state or props (react-base/RULES.md § Components: "a component body... derives trivial display
// values inline"; a multi-branch parse is not trivial, so it belongs here instead), which also
// makes every one of them testable without rendering anything.

const stringParamSchema = z.string().catch('');

/** A free-text filter (a search box) — missing or malformed falls back to "no filter". */
export const parseStringParam = (params: URLSearchParams, key: string): string =>
  stringParamSchema.parse(params.get(key) ?? undefined);

const booleanParamSchema = (defaultValue: boolean) =>
  z
    .enum(['true', 'false'])
    .catch(defaultValue ? 'true' : 'false')
    .transform(value => value === 'true');

/** A checkbox-style filter — stored as the literal strings "true"/"false", never a bare boolean
 * (`z.coerce.boolean()` would read any non-empty string, including "false", as `true`). */
export const parseBooleanParam = (
  params: URLSearchParams,
  key: string,
  defaultValue = false
): boolean => booleanParamSchema(defaultValue).parse(params.get(key) ?? undefined);

/** A single-select filter with a fixed option list (e.g. "ALL" plus one value per enum member). */
export const parseEnumParam = <T extends string>(
  params: URLSearchParams,
  key: string,
  values: readonly [T, ...T[]],
  defaultValue: T
): T =>
  z
    .enum(values)
    .catch(defaultValue)
    .parse(params.get(key) ?? undefined);

const calendarDayParamSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(value => dayjs(value).format('YYYY-MM-DD') === value)
  .optional()
  .catch(undefined);

/** A single calendar-day filter, stored as "YYYY-MM-DD" (the Agent log's date filter) — missing
 * or malformed, including an impossible day such as 2026-02-30, falls back to "no filter" rather
 * than reaching dayjs as an invalid date. */
export const parseCalendarDayParam = (params: URLSearchParams, key: string): string | undefined =>
  calendarDayParamSchema.parse(params.get(key) ?? undefined);

const positiveIntParamSchema = z.coerce.number().int().positive().optional().catch(undefined);

/** A numeric id filter set by navigation (the Agent log's conversation filter) — missing or
 * malformed falls back to "no filter" rather than sending `NaN` to the API. */
export const parsePositiveIntParam = (params: URLSearchParams, key: string): number | undefined =>
  positiveIntParamSchema.parse(params.get(key) ?? undefined);

/** A multi-select filter (Orders' status filter): a comma-separated list in one param, each value
 * checked against `values`; an unrecognised entry is dropped rather than failing the whole list. */
export const parseEnumListParam = <T extends string>(
  params: URLSearchParams,
  key: string,
  values: readonly [T, ...T[]],
  defaultValue: readonly T[]
): T[] => {
  const raw = params.get(key);
  if (raw === null) return [...defaultValue];
  const itemSchema = z.enum(values);
  return raw
    .split(',')
    .map(value => itemSchema.safeParse(value))
    .filter((result): result is { success: true; data: T } => result.success)
    .map(result => result.data);
};

export type SortDirection = 'asc' | 'desc';
export type SortParam<T extends string> = { field: T; sort: SortDirection };

/**
 * REQ-7/REQ-8 (owner decision 2026-10-24, docs/session-handoff.md: column sorting on the
 * Customers, Orders and Subscriptions tables happens on the server): parses the `sort` search
 * param (`<field>,asc|desc`, the exact string apps/api's controllers accept, SHARED_CONTRACTS
 * round 2 "Sorting") against the one endpoint's own accepted field list. An unrecognised field, a
 * missing/invalid direction, or no param at all falls back to that endpoint's own default -- the
 * same default the API applies when no `sort` reaches it, so a stale deep link never asks the
 * server for a field it has since stopped accepting.
 */
export const parseSortParam = <T extends string>(
  params: URLSearchParams,
  key: string,
  fields: readonly [T, ...T[]],
  defaultValue: SortParam<T>
): SortParam<T> => {
  const raw = params.get(key);
  if (raw === null) return defaultValue;
  const [field, direction] = raw.split(',');
  const fieldResult = z.enum(fields).safeParse(field);
  const directionResult = z.enum(['asc', 'desc']).safeParse(direction);
  if (!fieldResult.success || !directionResult.success) return defaultValue;
  return { field: fieldResult.data, sort: directionResult.data };
};

/** The inverse of {@link parseSortParam} -- the exact `<field>,asc|desc` string the API accepts. */
export const formatSortParam = (model: SortParam<string>): string => `${model.field},${model.sort}`;

/**
 * Turns a DataGrid `onSortModelChange` entry (its own three-state cycle: asc, desc, then cleared)
 * into the next `sort` search-param value to commit -- the clicked field and direction when the
 * field is one this endpoint actually accepts, the endpoint's own default otherwise. MUI's
 * "cleared" state (the model's third click) and an unsortable column (never produces an entry
 * here in the first place, since its `GridColDef` never sets `sortable: true`) both land on the
 * same default a fresh visit already had.
 */
export const nextSortParamValue = <T extends string>(
  entry: { field: string; sort: SortDirection | null | undefined } | undefined,
  fields: readonly [T, ...T[]],
  defaultValue: SortParam<T>
): string => {
  if (entry?.sort) {
    const fieldResult = z.enum(fields).safeParse(entry.field);
    if (fieldResult.success) return formatSortParam({ field: fieldResult.data, sort: entry.sort });
  }
  return formatSortParam(defaultValue);
};

/**
 * Applies one or more filter changes to a copy of `current` and resets `pageKey` to `0` in the
 * same update — a filter change and the page-reset it causes must land in one `setSearchParams`
 * call, because two separate calls in the same handler can each start from the same pre-update
 * snapshot and clobber each other (react-router's `setSearchParams`, like React state, does not
 * guarantee the second updater in one tick sees the first's result).
 */
export const setFilterParams = (
  current: URLSearchParams,
  updates: Record<string, string | null>,
  pageKey: string
): URLSearchParams => {
  const next = new URLSearchParams(current);
  for (const [key, value] of Object.entries(updates)) {
    if (value === null) next.delete(key);
    else next.set(key, value);
  }
  next.set(pageKey, '0');
  return next;
};
